import { useEffect, useRef, useState } from "react";

type UseExploreInViewOptions = {
  /** Fraction of element that must be visible (0–1) */
  threshold?: number;
  /** Only trigger once (default true) */
  once?: boolean;
  rootMargin?: string;
};

/**
 * IntersectionObserver helper for Explore scroll reveals.
 */
export function useExploreInView<T extends HTMLElement = HTMLDivElement>({
  threshold = 0.25,
  once = true,
  rootMargin = "0px 0px -8% 0px",
}: UseExploreInViewOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setIsInView(false);
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, once, rootMargin]);

  return { ref, isInView };
}
